import { useMemo } from 'react';
import { FunctionField, NumberField, TextField, TopToolbar, usePermissions, useTranslate } from 'react-admin';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { BrandLogoField } from '@/components/brand/fields/BrandLogoField.jsx';
import { BrandRetailChainField } from '@/components/brand/fields/BrandRetailChainField.jsx';
import { BrandMoreActions } from '@/components/brand/buttons/BrandMoreActions.jsx';
import { BRAND_SEARCH_FIELDS, getBrandFilters } from '@/components/brand/list/brandFilters.jsx';
import { useDrawer } from '@/composables/useDrawer';

const ListActions = () => {
    const { permissions } = usePermissions();
    const translate = useTranslate();

    return (
        <TopToolbar>
            <SearchFilterInput searchFields={BRAND_SEARCH_FIELDS} />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {permissions?.isAdmin ? (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.brand.labels.create')} />
                </CustomToolbarActionsPopover>
            ) : null}
        </TopToolbar>
    );
};

ListActions.propTypes = {};

export const BrandList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const filters = useMemo(() => getBrandFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) {
        return null;
    }

    if (!permissions?.isAdmin && !permissions?.isBok) {
        return null;
    }

    return (
        <CustomList
            resource='brand'
            title={translate('menu.brands')}
            filters={filters}
            actions={<ListActions />}
            sort={{ field: 'name', order: 'ASC' }}
            empty={false}
        >
            <CustomDatagridConfigurable
                header={<CustomDatagridHeader />}
                bulkActionButtons={false}
                rowClick={id => {
                    openDrawer(id, 'brand-show');
                    return false;
                }}
            >
                <FunctionField
                    source='logo'
                    label={translate('resources.brand.fields.logo')}
                    sortable={false}
                    render={record => <BrandLogoField key={record?.id} />}
                />
                <TextField source='name' label={translate('resources.brand.fields.name')} sx={{ fontWeight: '600' }} />
                <FunctionField
                    source='retailChainId'
                    label={translate('resources.brand.fields.retailChain')}
                    sortBy='retailChain.name'
                    render={() => <BrandRetailChainField />}
                />
                <NumberField source='contractorCount' label={translate('resources.brand.fields.contractorCount')} emptyText='0' />
                <NumberField source='collectionPointCount' label={translate('resources.brand.fields.collectionPointCount')} emptyText='0' />
                <FunctionField
                    label={translate('common.options')}
                    sortable={false}
                    render={() => (
                        <Box onClick={event => event.stopPropagation()}>
                            <BrandMoreActions />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

BrandList.propTypes = {};
